import cloneDeep from 'clone-deep'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { getLoanPaymentDistributionPreview } from 'services/apis/loan/servicing'
import { Button, Checkbox, Input2, Modal, PlainTable } from 'stories/components'
import { removeComma, thousandSeperatorWithPrefix } from 'utils'

/** One participant's share of the payment, mirroring the backend ComputedParticipantDistribution. */
export interface DistributionRow {
  participationId: number
  participantUserId: number | null
  participantName: string
  ownershipPct: number
  principal: number
  interest: number
  fees: number
  servicingFee: number
  amount: number
  printCheck: boolean
}

/** The borrower-paid servicing fee, owed to the company rather than to any participant. */
export interface CompanyFeeRow {
  payeeName: string
  amount: number
  /** Whether it will enter the check queue (off when the tenant runs payouts outside the system). */
  printCheck: boolean
}

/**
 * One payee's share of a fee the payment collected (a charge or the late charge), queued to that
 * payee by the fee's split rather than distributed to the participants.
 */
export interface FeePayoutRow {
  payeeName: string
  category: string
  percent: number
  amount: number
  printCheck: boolean
}

/** The three components that must reconcile to the computed split. Servicing fee is not one. */
type Component = 'principal' | 'interest' | 'fees'
const COMPONENTS: Component[] = ['principal', 'interest', 'fees']

const money = (v: any) => Number(removeComma(String(v ?? ''))) || 0

/** Net take = principal + interest + fees − servicing fee, held to cents (the entity invariant). */
const net = (r: DistributionRow) =>
  Math.round((money(r.principal) + money(r.interest) + money(r.fees) - money(r.servicingFee)) * 100) / 100

const sumOf = (rows: DistributionRow[], key: Component) =>
  Math.round(rows.reduce((s, r) => s + money(r[key]), 0) * 100) / 100

const normalize = (list: any[]): DistributionRow[] =>
  list.map((d) => ({
    participationId: Number(d.participationId),
    participantUserId: d.participantUserId ?? null,
    participantName: d.participantName || '',
    ownershipPct: Number(d.ownershipPct) || 0,
    principal: Number(d.principal) || 0,
    interest: Number(d.interest) || 0,
    fees: Number(d.fees) || 0,
    servicingFee: Number(d.servicingFee) || 0,
    amount: Number(d.amount) || 0,
    printCheck: !!d.printCheck,
  }))

/**
 * Preview and edit how a payment will be split across the loan's participants before it is applied.
 * Opens on the computed ownership split (from POST loanPayment/distributionPreview); the user can
 * reallocate each participant's principal / interest / fees and adjust the servicing fee. The edited
 * principal, interest and fees must each still total the computed amounts (the same money leaves to
 * investors, just divided differently) — the backend enforces the same rule. `onClose(rows)` returns
 * the edited rows to attach as `participantDistributions` (or null when cancelled / nothing to
 * distribute, keeping the automatic split).
 *
 * `paymentId` is set only when editing a payment that is already applied. The preview needs it to
 * split on the basis that payment was distributed on; without it an applied payoff previews against
 * a zero balance, so no participant has a share and there is nothing to reconcile to or confirm.
 */
export const DistributionsModal = ({
  paymentData,
  paymentId,
  initialRows,
  onClose,
}: {
  paymentData: Record<string, any>
  paymentId?: number
  initialRows: DistributionRow[] | null
  onClose: (rows: DistributionRow[] | null) => void
}) => {
  const [rows, setRows] = useState<DistributionRow[]>(initialRows || [])
  // Read-only: the servicing fee the borrower paid, which goes to the company, not to participants.
  const [companyFee, setCompanyFee] = useState<CompanyFeeRow | null>(null)
  // Read-only: the fee money the payment queues to other payees by each fee's split.
  const [feePayouts, setFeePayouts] = useState<FeePayoutRow[]>([])
  // The computed split's per-component totals — the targets the edits must reconcile to.
  const [targets, setTargets] = useState<Record<Component, number> | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchPreview = (adoptRows: boolean) => {
    setLoading(true)
    getLoanPaymentDistributionPreview(paymentData, paymentId)
      .then((res: any) => {
        const computed = normalize(Array.isArray(res?.distributions) ? res.distributions : [])
        setCompanyFee(
          res?.companyFee
            ? {
                payeeName: res.companyFee.payeeName || 'Company',
                amount: Number(res.companyFee.amount) || 0,
                printCheck: !!res.companyFee.printCheck,
              }
            : null,
        )
        setFeePayouts(
          (Array.isArray(res?.feePayouts) ? res.feePayouts : []).map((p: any) => ({
            payeeName: p.payeeName || '',
            category: p.category || '',
            percent: Number(p.percent) || 0,
            amount: Number(p.amount) || 0,
            printCheck: !!p.printCheck,
          })),
        )
        setTargets({
          principal: sumOf(computed, 'principal'),
          interest: sumOf(computed, 'interest'),
          fees: sumOf(computed, 'fees'),
        })
        if (adoptRows) setRows(computed)
      })
      .catch(() => toast('Could not load the distribution preview.', { type: 'error' }))
      .finally(() => setLoading(false))
  }

  // Always fetch on mount to learn the reconcile targets. Re-opening with prior edits keeps those
  // rows (adoptRows = false); a first open adopts the computed split as the starting point.
  useEffect(() => {
    fetchPreview(!initialRows)
  }, [])

  const setField = (idx: number, field: keyof DistributionRow, value: any) => {
    setRows((prev) => {
      const next = cloneDeep(prev)
      ;(next[idx] as any)[field] = value
      next[idx].amount = net(next[idx])
      return next
    })
  }

  // Per-row rounding means the sum can drift by up to a cent per row; allow that much.
  const tolerance = 0.01 * Math.max(rows.length, 1)
  const distributed: Record<Component, number> = {
    principal: sumOf(rows, 'principal'),
    interest: sumOf(rows, 'interest'),
    fees: sumOf(rows, 'fees'),
  }
  const offBy = (c: Component) => (targets ? Math.round((distributed[c] - targets[c]) * 100) / 100 : 0)
  const reconciled = !!targets && COMPONENTS.every((c) => Math.abs(offBy(c)) <= tolerance)
  // The payment's whole distribution: what the participants take, the company's servicing fee and
  // the fee payouts.
  const netTotal =
    rows.reduce((s, r) => s + net(r), 0) + (companyFee?.amount || 0) + feePayouts.reduce((s, p) => s + p.amount, 0)

  const moneyCell = (idx: number, field: Component | 'servicingFee') => (
    <div className="w-28">
      <Input2
        type="thousandSep"
        prefix="$"
        value={String(rows[idx][field] ?? '')}
        onChange={(v: string) => setField(idx, field, money(v))}
      />
    </div>
  )

  const header = ['Participant', 'Ownership %', 'Principal', 'Interest', 'Fees', 'Servicing Fee', 'Net', 'Pay?']

  // Totals row: each component shows its target so a mismatch is obvious at a glance.
  const totalCell = (c: Component) => {
    const bad = Math.abs(offBy(c)) > tolerance
    return (
      <span className={bad ? 'font-semibold text-rose-600' : 'font-semibold'}>
        {thousandSeperatorWithPrefix(distributed[c])}
        {targets ? <span className="block text-[11px] font-normal text-gray-500">of {thousandSeperatorWithPrefix(targets[c])}</span> : null}
      </span>
    )
  }

  // The company's own line: the borrower-paid servicing fee. Read-only — it isn't part of the
  // ownership split and doesn't reconcile, it just has to be visible and accounted for.
  const companyRow = companyFee
    ? [
        [
          <span className="italic">{`${companyFee.payeeName} (Servicing Fee)`}</span>,
          '',
          '',
          '',
          '',
          thousandSeperatorWithPrefix(companyFee.amount),
          thousandSeperatorWithPrefix(companyFee.amount),
          <span className={companyFee.printCheck ? 'text-xs' : 'text-xs text-gray-500'}>
            {companyFee.printCheck ? 'Queued' : 'Not queued'}
          </span>,
        ],
      ]
    : []

  // Each fee payout on its own line, read-only like the servicing fee. Only Net carries the amount:
  // the Fees column totals what the participants take, and this money is not theirs.
  const feePayoutRows = feePayouts.map((p) => [
    <span className="italic">{`${p.payeeName} (${p.category || 'Fee'} ${p.percent}%)`}</span>,
    '',
    '',
    '',
    '',
    '',
    thousandSeperatorWithPrefix(p.amount),
    <span className={p.printCheck ? 'text-xs' : 'text-xs text-gray-500'}>{p.printCheck ? 'Queued' : 'Not queued'}</span>,
  ])

  const data = rows.length || companyFee || feePayouts.length
    ? [
        ...rows.map((r, i) => [
          r.participantName || `Participation #${r.participationId}`,
          r.ownershipPct != null ? `${Number(r.ownershipPct)}%` : '',
          moneyCell(i, 'principal'),
          moneyCell(i, 'interest'),
          moneyCell(i, 'fees'),
          moneyCell(i, 'servicingFee'),
          thousandSeperatorWithPrefix(net(r)),
          <Checkbox
            id={`printCheck-${r.participationId}`}
            key={`printCheck-${r.participationId}`}
            checked={r.printCheck}
            onChange={(checked: boolean) => setField(i, 'printCheck', checked)}
            className="w-auto"
          />,
        ]),
        ...companyRow,
        ...feePayoutRows,
        [
          <span className="font-semibold">Total</span>,
          '',
          totalCell('principal'),
          totalCell('interest'),
          totalCell('fees'),
          '',
          <span className="font-semibold">{thousandSeperatorWithPrefix(netTotal)}</span>,
          '',
        ],
      ]
    : [[loading ? 'Loading…' : 'No participants to distribute to on this loan.', '', '', '', '', '', '', '']]

  return (
    <Modal
      title="Participant Distributions"
      isOpen={true}
      onClose={() => onClose(null)}
      onOk={() => onClose(rows)}
      titleOkay="Use these distributions"
      disabled={loading || !rows.length || !reconciled}
      loading={loading}
    >
      <div className="text-sm">
        <div className="flex items-center justify-between mb-2 gap-4">
          <p className="text-gray-600">
            How this payment splits across the loan&apos;s participants by ownership. Reallocate any amount between
            participants; Principal, Interest and Fees must each still total the computed amount.
          </p>
          <Button link onClick={() => fetchPreview(true)} disabled={loading}>
            Reset to computed
          </Button>
        </div>
        {!!rows.length && targets && !reconciled && (
          <div className="mb-2 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-rose-700 text-xs">
            {COMPONENTS.filter((c) => Math.abs(offBy(c)) > tolerance).map((c) => {
              const diff = offBy(c)
              const label = c.charAt(0).toUpperCase() + c.slice(1)
              return (
                <div key={c}>
                  {label} is {thousandSeperatorWithPrefix(Math.abs(diff))} {diff > 0 ? 'over' : 'under'} the computed
                  total of {thousandSeperatorWithPrefix(targets[c])}.
                </div>
              )
            })}
          </div>
        )}
        <div className="relative max-h-96 overflow-auto">
          <PlainTable header={header} tdClass="px-4 py-2 whitespace-nowrap" data={data} />
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Only the principal portion reduces a participant&apos;s outstanding balance. Servicing fee is the
          servicer&apos;s retained cut (it does not need to reconcile). Uncheck &quot;Pay?&quot; to keep a row out of
          the check queue. Net = Principal + Interest + Fees − Servicing Fee.
        </p>
        {!!companyFee && (
          <p className="text-xs text-gray-500 mt-1">
            The servicing fee the borrower paid goes to {companyFee.payeeName}, not to the participants
            {companyFee.printCheck
              ? ', and is queued in the Check Queue when the payment is applied.'
              : '. Payout queuing is turned off for this company, so it will not queue a check.'}
          </p>
        )}
        {!!feePayouts.length && (
          <p className="text-xs text-gray-500 mt-1">
            Fee lines go to the payee each fee&apos;s split names, not to the participants
            {feePayouts.some((p) => p.printCheck)
              ? ', and are queued in the Check Queue when the payment is applied.'
              : '. Payout queuing is turned off for this company, so they will not queue a check.'}
          </p>
        )}
      </div>
    </Modal>
  )
}
